/**
 * API helpers. Mirror casey-platform/frontend/src/lib/api.ts conventions:
 * always use these helpers (never raw fetch) so credentials, content-type,
 * and base-URL handling are consistent.
 *
 * Backend lives at:
 *   - dev: http://127.0.0.1:5000 (proxied via Vite dev server — same-origin)
 *   - prod: https://api.cait.academy (set via VITE_API_BASE_URL)
 *
 * `credentials: "include"` is mandatory so first-party cookies (consent,
 * session) round-trip on cross-origin requests in production.
 */

/**
 * Backend API base URL. Empty string in local dev (Vite proxies /api/* to
 * 127.0.0.1:5000 same-origin); set to ``https://api.cait.clinicianassist.ai``
 * in production builds via ``VITE_API_BASE_URL``.
 *
 * Exported so non-fetch call sites (``window.location.href`` redirects to
 * /api/auth/login, etc.) can prepend the base URL the same way ``apiGet``
 * and friends do — otherwise the redirect goes to the Pages origin and
 * never reaches the backend (the SPA's React Router has no route for
 * /api/auth/login, so it just renders blank).
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

const BASE_URL: string = API_BASE_URL;

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...(init.headers ?? {}),
  };

  const response = await fetch(url, {
    ...init,
    headers,
    credentials: "include",
  });

  let body: unknown = null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    body = await response.json();
  } else if (response.status !== 204) {
    body = await response.text();
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body,
      `${init.method ?? "GET"} ${path} → ${response.status}`,
    );
  }

  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: "GET" });
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  // exactOptionalPropertyTypes: build init conditionally so we never pass
  // ``body: undefined`` (which RequestInit forbids under strict optional types).
  const init: RequestInit = { method: "POST" };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return request<T>(path, init);
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method: "PUT" };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return request<T>(path, init);
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method: "PATCH" };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return request<T>(path, init);
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}

/**
 * Attempt a silent WorkOS sealed-session refresh and return whether the
 * server re-minted the session cookie. Never throws — callers use it to
 * recover from a 401 mid-flow (e.g. a long exam whose access token expired)
 * by refreshing and retrying the original request before falling back to a
 * full AuthKit re-auth redirect.
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
    return response.ok;
  } catch {
    return false;
  }
}

export { ApiError };
